'use client'

import { useState, useMemo, useCallback } from 'react'
import styles from './TerminalTabs.module.css'
import { useBinanceOrderHistory } from './hooks/useBinanceData'
import { useSearchParams } from 'next/navigation'

/**
 * @uiComponent
 * @name OrderHistory
 * @description The OrderHistory component displays historical cryptocurrency trading orders on the Binance platform.
 * It shows completed, cancelled, and rejected orders with detailed information including execution prices,
 * quantities, and timestamps. Features symbol filtering, custom pair input, and trading statistics
 * like average buy/sell prices and net positions for cryptocurrency pairs.
 * @location Order History tab in the trading terminal
 * @prop none - No external props, manages internal state and connector integration
 */

/**
 * @uiWorkflow
 * @name Review Crypto Trading History on Binance
 * @description How to analyze your past cryptocurrency trading activity and performance
 * @platform binance
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "Order History" tab [component: OrderHistory] [action: click]
 * @step 3 Review all past orders sorted by most recent first [component: OrderHistory] [action: observe]
 * @step 4 Use the symbol dropdown to filter by specific crypto pairs [component: OrderHistory] [action: select]
 * @step 5 Check order status (FILLED, CANCELLED, REJECTED, etc.) [component: OrderHistory] [action: observe]
 * @step 6 Compare order price vs executed price and quantities [component: OrderHistory] [action: observe]
 * @step 7 Review trading statistics in the summary section [component: OrderHistory] [action: observe]
 * @step 8 Use data to analyze trading patterns and performance [component: OrderHistory] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Analyze Trading Performance by Crypto Pair on Binance
 * @description How to evaluate your trading performance for specific cryptocurrency pairs
 * @platform binance
 * @step 1 Open the Order History tab [component: OrderHistory] [action: click]
 * @step 2 Select a specific crypto pair from the dropdown [component: OrderHistory] [action: select]
 * @step 3 Review the trading summary showing buy/sell statistics [component: OrderHistory] [action: observe]
 * @step 4 Check total buy quantity vs total sell quantity [component: OrderHistory] [action: observe]
 * @step 5 Compare average buy price vs average sell price [component: OrderHistory] [action: observe]
 * @step 6 Calculate your net position (buy qty - sell qty) [component: OrderHistory] [action: observe]
 * @step 7 Review individual order execution details [component: OrderHistory] [action: scroll]
 * @step 8 Use this analysis to improve future crypto trading decisions [component: OrderHistory] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Search Custom Crypto Pair History on Binance
 * @description How to view order history for cryptocurrency pairs not in the common list
 * @platform binance
 * @step 1 Access the Order History tab [component: OrderHistory] [action: click]
 * @step 2 Click on the symbol dropdown menu [component: OrderHistory] [action: click]
 * @step 3 Select "Custom Pair" from the dropdown options [component: OrderHistory] [action: select]
 * @step 4 Enter the crypto pair symbol in the custom input field [component: OrderHistory] [action: input] [notes: e.g., SOLUSDT, ADABTC, DOTUSDT]
 * @step 5 Click the "Go" button to search for that pair's history [component: OrderHistory] [action: click]
 * @step 6 Review the filtered order history for the custom pair [component: OrderHistory] [action: observe]
 * @step 7 The pair will be added to your view for future reference [component: OrderHistory] [action: observe]
 */

interface OrderHistoryItem {
  symbol: string
  orderId: number
  orderListId: number
  clientOrderId: string
  price: string
  origQty: string
  executedQty: string
  cummulativeQuoteQty: string
  status: string
  timeInForce: string
  type: string
  side: string
  stopPrice: string
  icebergQty: string
  time: number
  updateTime: number
  isWorking: boolean
  origQuoteOrderQty: string
}

// Common trading pairs for the dropdown
const COMMON_PAIRS = [
  'BTCUSDT',
  'ETHUSDT',
  'BNBUSDT',
  'ADAUSDT',
  'DOGEUSDT',
  'XRPUSDT',
  'DOTUSDT',
  'UNIUSDT',
  'LTCUSDT',
  'LINKUSDT',
  'BTCBUSD',
  'ETHBTC',
  'BNBBTC'
]

export default function OrderHistory() {
  const searchParams = useSearchParams()

  // Get symbol from URL path instead of query parameters
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const pathSymbol = pathname.split('/').pop()?.toUpperCase()

  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  const [selectedSymbol, setSelectedSymbol] = useState(urlSymbol || 'BTCUSDT')
  const [customSymbol, setCustomSymbol] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)

  // Use React Query hook instead of useEffect for data fetching
  const { data: rawOrders = [], isLoading, error } = useBinanceOrderHistory(selectedSymbol)

  // Sort orders by time using useMemo for performance
  const orders = useMemo(() => {
    return [...rawOrders].sort((a, b) => b.time - a.time)
  }, [rawOrders])

  // Calculate trading metrics using useMemo for performance
  const tradingMetrics = useMemo(() => {
    // Calculate metrics for the selected symbol
    const filledOrders = orders.filter(order => order.status === 'FILLED')

    // Calculate total buy and sell quantities
    const buyOrders = filledOrders.filter(order => order.side === 'BUY')
    const sellOrders = filledOrders.filter(order => order.side === 'SELL')

    const totalBuyQty = buyOrders.reduce(
      (sum, order) => sum + parseFloat(order.executedQty),
      0
    )
    const totalSellQty = sellOrders.reduce(
      (sum, order) => sum + parseFloat(order.executedQty),
      0
    )

    // Calculate total buy and sell values
    const totalBuyValue = buyOrders.reduce(
      (sum, order) => sum + parseFloat(order.cummulativeQuoteQty),
      0
    )
    const totalSellValue = sellOrders.reduce(
      (sum, order) => sum + parseFloat(order.cummulativeQuoteQty),
      0
    )

    // Calculate average prices
    const avgBuyPrice = totalBuyQty > 0 ? totalBuyValue / totalBuyQty : 0
    const avgSellPrice = totalSellQty > 0 ? totalSellValue / totalSellQty : 0

    // Calculate overall average price
    const totalQty = totalBuyQty + totalSellQty
    const totalValue = totalBuyValue + totalSellValue
    const avgPrice = totalQty > 0 ? totalValue / totalQty : 0

    return {
      filledOrders,
      totalBuyQty,
      totalSellQty,
      totalBuyValue,
      totalSellValue,
      avgBuyPrice,
      avgSellPrice,
      avgPrice
    }
  }, [orders])

  const handleSymbolChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === 'custom') {
      setShowCustomInput(true)
    } else {
      setShowCustomInput(false)
      setSelectedSymbol(value)
    }
  }, [])

  const handleCustomSymbolSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (customSymbol) {
      setSelectedSymbol(customSymbol.toUpperCase())
      setCustomSymbol('')
      setShowCustomInput(false)
    }
  }, [customSymbol])

  if (isLoading) {
    return <div className={styles.loadingState}>Loading order history...</div>
  }
  
  if (error) {
    return <div className={styles.errorState}>{error instanceof Error ? error.message : String(error)}</div>
  }

  const {
    filledOrders,
    totalBuyQty,
    totalSellQty,
    totalBuyValue,
    totalSellValue,
    avgPrice
  } = tradingMetrics

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={
              showCustomInput
                ? 'custom'
                : COMMON_PAIRS.includes(selectedSymbol)
                  ? selectedSymbol
                  : 'custom'
            }
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
          >
            {COMMON_PAIRS.map(pair => (
              <option key={pair} value={pair}>
                {pair}
              </option>
            ))}
            <option value="custom">Custom Pair</option>
          </select>

          {(showCustomInput || !COMMON_PAIRS.includes(selectedSymbol)) && (
            <form
              onSubmit={handleCustomSymbolSubmit}
              className={styles.customSymbolForm}
            >
              <input
                type="text"
                value={
                  customSymbol ||
                  (!COMMON_PAIRS.includes(selectedSymbol) && !showCustomInput
                    ? selectedSymbol
                    : '')
                }
                onChange={e => setCustomSymbol(e.target.value)}
                placeholder="Enter symbol (e.g. SOLUSDT)"
                className={styles.customSymbolInput}
                autoFocus
              />
              <button type="submit" className={styles.submitButton}>
                Go
              </button>
            </form>
          )}
        </div>
        <div className={styles.currentSymbol}>
          Current: <strong>{selectedSymbol}</strong>
          {filledOrders.length > 0 && (
            <>
              {' | '}
              Total Buy:{' '}
              <strong className={styles.buyOrder}>
                {totalBuyQty.toFixed(4)}
              </strong>{' '}
              (${totalBuyValue.toFixed(2)}){' | '}
              Total Sell:{' '}
              <strong className={styles.sellOrder}>
                {totalSellQty.toFixed(4)}
              </strong>{' '}
              (${totalSellValue.toFixed(2)}){' | '}
              Avg Trade Price:{' '}
              <strong>
                ${avgPrice.toFixed(avgPrice < 1 ? 8 : 2)}
              </strong>
              {' | '}
              Net:{' '}
              <strong
                className={
                  totalBuyQty > totalSellQty
                    ? styles.buyOrder
                    : styles.sellOrder
                }
              >
                {Math.abs(totalBuyQty - totalSellQty).toFixed(4)}
              </strong>
            </>
          )}
        </div>
      </div>

      {orders.length === 0 ? (
        <div className={styles.emptyState}>
          No order history found for {selectedSymbol}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Symbol</th>
                <th>Type</th>
                <th>Side</th>
                <th>Price</th>
                <th>Amount</th>
                <th>Filled</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => {
                const date = new Date(order.time)
                const formattedDate = date.toLocaleString()

                // For MARKET orders, price might be 0, so calculate from cummulativeQuoteQty
                let price = parseFloat(order.price)
                const amount = parseFloat(order.origQty)
                const filled = parseFloat(order.executedQty)

                // Calculate total using cummulativeQuoteQty for more accuracy
                const total = parseFloat(order.cummulativeQuoteQty)

                // For MARKET orders, calculate the average price
                if (order.type === 'MARKET' && price === 0 && filled > 0) {
                  price = total / filled
                }

                return (
                  <tr key={order.orderId}>
                    <td>{formattedDate}</td>
                    <td>{order.symbol}</td>
                    <td>{order.type}</td>
                    <td
                      className={
                        order.side === 'BUY'
                          ? styles.buyOrder
                          : styles.sellOrder
                      }
                    >
                      {order.side}
                    </td>
                    <td>${price.toFixed(price < 1 ? 8 : 2)}</td>
                    <td>{amount.toFixed(8)}</td>
                    <td>{filled.toFixed(8)}</td>
                    <td>${total.toFixed(2)}</td>
                    <td
                      className={
                        order.status === 'FILLED'
                          ? styles.statusFilled
                          : order.status === 'CANCELED'
                            ? styles.statusCanceled
                            : styles.statusOther
                      }
                    >
                      {order.status}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
